import React, { useState, useEffect } from 'react';
import { Post, PostCategory, CategoryInfo, CreatePostData, Comment, CreateCommentData } from '../types';
import { postsApi, commentsApi } from '../api';
import { useAuth } from '../AuthContext';
import { theme } from '../theme';

const BulletinBoard: React.FC = () => {
    const [posts, setPosts] = useState<Post[]>([]);
    const [categories, setCategories] = useState<CategoryInfo[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<PostCategory | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingPost, setEditingPost] = useState<Post | null>(null);
    const { isAdmin, user } = useAuth();

    useEffect(() => {
        loadData();
    }, [selectedCategory]);

    const loadData = async () => {
        try {
            setIsLoading(true);
            const [postsData, categoriesData] = await Promise.all([
                postsApi.getPosts(selectedCategory),
                postsApi.getCategories()
            ]);
            setPosts(postsData);
            setCategories(categoriesData);
        } catch (err) {
            setError('Kunne ikke laste innlegg');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Er du sikker på at du vil slette dette innlegget?')) {
            return;
        }
        try {
            await postsApi.deletePost(id);
            setPosts(posts.filter(p => p.id !== id));
        } catch (err) {
            setError('Kunne ikke slette innlegget');
        }
    };

    const handleTogglePin = async (post: Post) => {
        try {
            const updated = await postsApi.updatePost(post.id, { isPinned: !post.isPinned });
            setPosts(posts.map(p => p.id === post.id ? updated : p));
        } catch (err) {
            setError('Kunne ikke oppdatere innlegget');
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('nb-NO', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getCategoryColor = (category: PostCategory): string => {
        const colors: { [key: number]: string } = {
            [PostCategory.General]: '#2196f3',
            [PostCategory.Maintenance]: '#ff9800',
            [PostCategory.Dugnad]: '#4caf50',
            [PostCategory.Outage]: '#f44336',
            [PostCategory.Practical]: '#9c27b0',
        };
        return colors[category] || '#757575';
    };

    if (isLoading) {
        return <div style={styles.loading}>Laster...</div>;
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Oppslagstavle</h1>
                <button
                    style={styles.createButton}
                    onClick={() => setShowCreateForm(true)}
                >
                    + Nytt innlegg
                </button>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.filters}>
                <button
                    style={{
                        ...styles.filterButton,
                        ...(selectedCategory === undefined ? styles.filterButtonActive : {})
                    }}
                    onClick={() => setSelectedCategory(undefined)}
                >
                    Alle
                </button>
                {categories.map(cat => (
                    <button
                        key={cat.value}
                        style={{
                            ...styles.filterButton,
                            ...(selectedCategory === cat.value ? styles.filterButtonActive : {})
                        }}
                        onClick={() => setSelectedCategory(cat.value)}
                    >
                        {cat.displayName}
                    </button>
                ))}
            </div>

            {showCreateForm && (
                <CreatePostForm
                    categories={categories}
                    onClose={() => setShowCreateForm(false)}
                    onCreated={(post) => {
                        setPosts([post, ...posts]);
                        setShowCreateForm(false);
                    }}
                    isAdmin={isAdmin}
                />
            )}

            {editingPost && (
                <EditPostForm
                    post={editingPost}
                    categories={categories}
                    onClose={() => setEditingPost(null)}
                    onUpdated={(updatedPost) => {
                        setPosts(posts.map(p => p.id === updatedPost.id ? updatedPost : p));
                        setEditingPost(null);
                    }}
                    isAdmin={isAdmin}
                />
            )}

            <div style={styles.posts}>
                {posts.length === 0 ? (
                    <div style={styles.empty}>Ingen innlegg å vise</div>
                ) : (
                    posts.map(post => (
                        <div key={post.id} style={styles.postCard}>
                            <div style={styles.postHeader}>
                                <div style={styles.postMeta}>
                                    <span
                                        style={{
                                            ...styles.categoryBadge,
                                            backgroundColor: getCategoryColor(post.category)
                                        }}
                                    >
                                        {post.categoryName}
                                    </span>
                                    {post.isPinned && (
                                        <span style={styles.pinnedBadge}>📌 Festet</span>
                                    )}
                                </div>
                                {isAdmin && (
                                    <div style={styles.postActions}>
                                        <button
                                            style={styles.actionButton}
                                            onClick={() => handleTogglePin(post)}
                                            title={post.isPinned ? 'Fjern festet' : 'Fest innlegg'}
                                        >
                                            {post.isPinned ? '📌' : '📍'}
                                        </button>
                                        <button
                                            style={styles.actionButton}
                                            onClick={() => setEditingPost(post)}
                                            title="Rediger"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            style={styles.actionButton}
                                            onClick={() => handleDelete(post.id)}
                                            title="Slett"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                )}
                                {!isAdmin && user?.id === post.authorId && (
                                    <div style={styles.postActions}>
                                        <button
                                            style={styles.actionButton}
                                            onClick={() => setEditingPost(post)}
                                            title="Rediger"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            style={styles.actionButton}
                                            onClick={() => handleDelete(post.id)}
                                            title="Slett"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                )}
                            </div>
                            <h2 style={styles.postTitle}>{post.title}</h2>
                            <div style={styles.postContent}>{post.content}</div>
                            <div style={styles.postFooter}>
                                <span>{post.authorName}</span>
                                <span>
                                    {formatDate(post.createdAt)}
                                    {post.updatedAt && <span style={styles.editedBadge}> (redigert)</span>}
                                </span>
                            </div>
                            <CommentSection postId={post.id} isAdmin={isAdmin} currentUserId={user?.id} />
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

interface CreatePostFormProps {
    categories: CategoryInfo[];
    onClose: () => void;
    onCreated: (post: Post) => void;
    isAdmin: boolean;
}

const CreatePostForm: React.FC<CreatePostFormProps> = ({ categories, onClose, onCreated, isAdmin }) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [category, setCategory] = useState<PostCategory>(PostCategory.General);
    const [isPinned, setIsPinned] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const data: CreatePostData = { title, content, category, isPinned };
            const post = await postsApi.createPost(data);
            onCreated(post);
        } catch (err) {
            setError('Kunne ikke opprette innlegget');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.modal}>
            <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                    <h2>Nytt innlegg</h2>
                    <button style={styles.closeButton} onClick={onClose}>✕</button>
                </div>

                {error && <div style={styles.error}>{error}</div>}

                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Tittel</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            style={styles.input}
                            required
                            maxLength={200}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Kategori</label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(Number(e.target.value))}
                            style={styles.select}
                        >
                            {categories.map(cat => (
                                <option key={cat.value} value={cat.value}>
                                    {cat.displayName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Innhold</label>
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            style={styles.textarea}
                            rows={6}
                            required
                        />
                    </div>

                    {isAdmin && (
                        <div style={styles.checkboxGroup}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={isPinned}
                                    onChange={(e) => setIsPinned(e.target.checked)}
                                />
                                {' '}Fest innlegget øverst
                            </label>
                        </div>
                    )}

                    <div style={styles.formActions}>
                        <button type="button" style={styles.cancelButton} onClick={onClose}>
                            Avbryt
                        </button>
                        <button type="submit" style={styles.submitButton} disabled={isLoading}>
                            {isLoading ? 'Publiserer...' : 'Publiser'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

interface EditPostFormProps {
    post: Post;
    categories: CategoryInfo[];
    onClose: () => void;
    onUpdated: (post: Post) => void;
    isAdmin: boolean;
}

const EditPostForm: React.FC<EditPostFormProps> = ({ post, categories, onClose, onUpdated, isAdmin }) => {
    const [title, setTitle] = useState(post.title);
    const [content, setContent] = useState(post.content);
    const [category, setCategory] = useState<PostCategory>(post.category);
    const [isPinned, setIsPinned] = useState(post.isPinned);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const data: Partial<CreatePostData> = { title, content, category };
            if (isAdmin) {
                data.isPinned = isPinned;
            }
            const updatedPost = await postsApi.updatePost(post.id, data);
            onUpdated(updatedPost);
        } catch (err) {
            setError('Kunne ikke oppdatere innlegget');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.modal}>
            <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                    <h2>Rediger innlegg</h2>
                    <button style={styles.closeButton} onClick={onClose}>✕</button>
                </div>

                {error && <div style={styles.error}>{error}</div>}

                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Tittel</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            style={styles.input}
                            required
                            maxLength={200}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Kategori</label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(Number(e.target.value))}
                            style={styles.select}
                        >
                            {categories.map(cat => (
                                <option key={cat.value} value={cat.value}>
                                    {cat.displayName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Innhold</label>
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            style={styles.textarea}
                            rows={6}
                            required
                        />
                    </div>

                    {isAdmin && (
                        <div style={styles.checkboxGroup}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={isPinned}
                                    onChange={(e) => setIsPinned(e.target.checked)}
                                />
                                {' '}Fest innlegget øverst
                            </label>
                        </div>
                    )}

                    <div style={styles.formActions}>
                        <button type="button" style={styles.cancelButton} onClick={onClose}>
                            Avbryt
                        </button>
                        <button type="submit" style={styles.submitButton} disabled={isLoading}>
                            {isLoading ? 'Lagrer...' : 'Lagre endringer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

interface CommentSectionProps {
    postId: number;
    isAdmin: boolean;
    currentUserId?: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({ postId, isAdmin, currentUserId }) => {
    const [comments, setComments] = useState<Comment[]>([]);
    const [isExpanded, setIsExpanded] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [newComment, setNewComment] = useState('');
    const [editingComment, setEditingComment] = useState<Comment | null>(null);
    const [editContent, setEditContent] = useState('');

    const loadComments = async () => {
        try {
            setIsLoading(true);
            const data = await commentsApi.getComments(postId);
            setComments(data);
            // Auto-expand if 1-5 comments, hide if 0 or more than 5
            if (isExpanded === null) {
                setIsExpanded(data.length > 0 && data.length <= 5);
            }
        } catch (err) {
            console.error('Could not load comments');
            if (isExpanded === null) {
                setIsExpanded(false);
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadComments();
    }, [postId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newComment.trim()) return;
        try {
            const comment = await commentsApi.createComment(postId, { content: newComment });
            setComments([...comments, comment]);
            setNewComment('');
        } catch (err) {
            console.error('Could not create comment');
        }
    };

    const handleUpdate = async (commentId: number) => {
        if (!editContent.trim()) return;
        try {
            const updated = await commentsApi.updateComment(postId, commentId, { content: editContent });
            setComments(comments.map(c => c.id === commentId ? updated : c));
            setEditingComment(null);
            setEditContent('');
        } catch (err) {
            console.error('Could not update comment');
        }
    };

    const handleDelete = async (commentId: number) => {
        if (!window.confirm('Slette denne kommentaren?')) return;
        try {
            await commentsApi.deleteComment(postId, commentId);
            setComments(comments.filter(c => c.id !== commentId));
        } catch (err) {
            console.error('Could not delete comment');
        }
    };

    const formatCommentDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('nb-NO', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const canEditOrDelete = (comment: Comment) => {
        return isAdmin || comment.authorId === currentUserId;
    };

    const hasComments = comments.length > 0;

    return (
        <div style={styles.commentSection}>
            <button 
                style={{
                    ...styles.toggleCommentsButton,
                    ...(hasComments ? styles.toggleCommentsButtonActive : {})
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                💬 {isExpanded ? 'Skjul kommentarer' : `Vis kommentarer (${comments.length})`}
            </button>

            {isExpanded && (
                <div style={styles.commentsContainer}>
                    {isLoading ? (
                        <div style={styles.commentsLoading}>Laster kommentarer...</div>
                    ) : (
                        <>
                            {comments.length === 0 ? (
                                <div style={styles.noComments}>Ingen kommentarer ennå</div>
                            ) : (
                                comments.map(comment => (
                                    <div key={comment.id} style={styles.comment}>
                                        {editingComment?.id === comment.id ? (
                                            <div style={styles.editCommentForm}>
                                                <textarea
                                                    value={editContent}
                                                    onChange={(e) => setEditContent(e.target.value)}
                                                    style={styles.commentTextarea}
                                                    rows={2}
                                                />
                                                <div style={styles.editCommentActions}>
                                                    <button 
                                                        style={styles.commentCancelButton}
                                                        onClick={() => { setEditingComment(null); setEditContent(''); }}
                                                    >
                                                        Avbryt
                                                    </button>
                                                    <button 
                                                        style={styles.commentSaveButton}
                                                        onClick={() => handleUpdate(comment.id)}
                                                    >
                                                        Lagre
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div style={styles.commentHeader}>
                                                    <span style={styles.commentAuthor}>{comment.authorName}</span>
                                                    <span style={styles.commentDate}>
                                                        {formatCommentDate(comment.createdAt)}
                                                        {comment.updatedAt && ' (redigert)'}
                                                    </span>
                                                </div>
                                                <div style={styles.commentContent}>{comment.content}</div>
                                                {canEditOrDelete(comment) && (
                                                    <div style={styles.commentActions}>
                                                        <button 
                                                            style={styles.commentActionButton}
                                                            onClick={() => { setEditingComment(comment); setEditContent(comment.content); }}
                                                        >
                                                            ✏️
                                                        </button>
                                                        <button 
                                                            style={styles.commentActionButton}
                                                            onClick={() => handleDelete(comment.id)}
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ))
                            )}

                            <form onSubmit={handleSubmit} style={styles.newCommentForm}>
                                <textarea
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Skriv en kommentar..."
                                    style={styles.commentTextarea}
                                    rows={2}
                                />
                                <button type="submit" style={styles.submitCommentButton} disabled={!newComment.trim()}>
                                    Kommenter
                                </button>
                            </form>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        maxWidth: '900px',
        margin: '0 auto',
        padding: '16px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
    },
    title: {
        margin: 0,
        color: '#333',
        fontSize: '24px',
    },
    createButton: {
        padding: '10px 20px',
        backgroundColor: theme.primaryColor,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
    },
    filters: {
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        overflowX: 'auto',
        paddingBottom: '8px',
        WebkitOverflowScrolling: 'touch',
    },
    filterButton: {
        padding: '8px 14px',
        backgroundColor: '#f5f5f5',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#ddd',
        borderRadius: '20px',
        cursor: 'pointer',
        fontSize: '13px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    filterButtonActive: {
        backgroundColor: theme.primaryColor,
        color: 'white',
        borderColor: theme.primaryColor,
    },
    loading: {
        textAlign: 'center',
        padding: '48px',
        color: '#666',
    },
    error: {
        padding: '12px',
        backgroundColor: '#ffebee',
        color: '#c62828',
        borderRadius: '4px',
        marginBottom: '16px',
    },
    empty: {
        textAlign: 'center',
        padding: '48px',
        color: '#666',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
    },
    posts: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    postCard: {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #eee',
    },
    postHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
    },
    postMeta: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
    },
    categoryBadge: {
        padding: '4px 12px',
        borderRadius: '12px',
        color: 'white',
        fontSize: '12px',
        fontWeight: 'bold',
    },
    pinnedBadge: {
        padding: '4px 8px',
        backgroundColor: '#fff3e0',
        color: '#e65100',
        borderRadius: '4px',
        fontSize: '12px',
    },
    postActions: {
        display: 'flex',
        gap: '4px',
    },
    actionButton: {
        padding: '4px 8px',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: '16px',
    },
    postTitle: {
        margin: '0 0 12px 0',
        fontSize: '18px',
        color: '#333',
        wordBreak: 'break-word',
    },
    postContent: {
        color: '#666',
        lineHeight: '1.6',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
    },
    postFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '16px',
        paddingTop: '12px',
        borderTop: '1px solid #eee',
        fontSize: '12px',
        color: '#999',
        flexWrap: 'wrap',
        gap: '4px',
    },
    editedBadge: {
        fontStyle: 'italic',
        color: '#999',
    },
    modal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '85vh',
        overflow: 'auto',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '20px',
        cursor: 'pointer',
        color: '#666',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    label: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#333',
    },
    input: {
        padding: '10px 12px',
        fontSize: '16px',
        border: '1px solid #ddd',
        borderRadius: '4px',
    },
    select: {
        padding: '10px 12px',
        fontSize: '16px',
        border: '1px solid #ddd',
        borderRadius: '4px',
    },
    textarea: {
        padding: '10px 12px',
        fontSize: '16px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        resize: 'vertical',
        fontFamily: 'inherit',
    },
    checkboxGroup: {
        fontSize: '14px',
    },
    formActions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '8px',
    },
    cancelButton: {
        padding: '10px 20px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '4px',
        cursor: 'pointer',
    },
    submitButton: {
        padding: '10px 20px',
        backgroundColor: theme.primaryColor,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 'bold',
    },
    // Comment styles
    commentSection: {
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: '1px solid #eee',
    },
    toggleCommentsButton: {
        background: 'none',
        border: 'none',
        color: '#666',
        cursor: 'pointer',
        fontSize: '14px',
        padding: '4px 0',
    },
    toggleCommentsButtonActive: {
        fontWeight: 'bold',
        fontSize: '15px',
        color: theme.primaryColor,
    },
    commentsContainer: {
        marginTop: '12px',
    },
    commentsLoading: {
        color: '#999',
        fontSize: '13px',
    },
    noComments: {
        color: '#999',
        fontSize: '13px',
        fontStyle: 'italic',
        marginBottom: '12px',
    },
    comment: {
        backgroundColor: '#f9f9f9',
        borderRadius: '6px',
        padding: '10px 12px',
        marginBottom: '8px',
    },
    commentHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '4px',
    },
    commentAuthor: {
        fontWeight: '500',
        fontSize: '13px',
        color: '#333',
    },
    commentDate: {
        fontSize: '11px',
        color: '#999',
    },
    commentContent: {
        fontSize: '14px',
        color: '#444',
        lineHeight: '1.4',
        wordBreak: 'break-word',
    },
    commentActions: {
        display: 'flex',
        gap: '4px',
        marginTop: '6px',
    },
    commentActionButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '12px',
        padding: '2px 4px',
    },
    newCommentForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: '12px',
    },
    commentTextarea: {
        padding: '8px 10px',
        fontSize: '14px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        resize: 'vertical',
        fontFamily: 'inherit',
    },
    submitCommentButton: {
        alignSelf: 'flex-end',
        padding: '6px 14px',
        backgroundColor: theme.primaryColor,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '13px',
    },
    editCommentForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    editCommentActions: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'flex-end',
    },
    commentCancelButton: {
        padding: '4px 10px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
    },
    commentSaveButton: {
        padding: '4px 10px',
        backgroundColor: theme.primaryColor,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
    },
};

export default BulletinBoard;
